import React, { useEffect } from "react";

import DropdownComponent from "../../dropdown";
import NumberDropdownComponent from "../../dropdown/number-dropdown";
import TimeDropdownComponent from "../../dropdown/time-dropdown";

import { IoMdArrowDropdown } from "react-icons/io";
import NoDataImg from "../../../../assets/data/nodata.png";
import { scrollToTop, selectedDropdown } from "../../../utils/helpers";
import ActiveDropdownComponent from "../../dropdown/boolean-dropdown";
import TableRowsLoader from "../loader/table-rows-loader";
import TableRow from "./table-row-companies";

/**
 * CompaniesDatatable Component
 * 
 * Renders interactive datatable for displaying companies dataset. Shows filtered/sorted
 * company records with dynamic columns based on hiddenFieldNames. Supports field sorting,
 * column visibility toggling, and pagination. Displays loading skeleton while data is fetching.
 * Shows no-data placeholder image when result set is empty. Maps through tableData array
 * to render individual company rows with dropdown controls.
 * 
 * @param {Object} props - Component props containing datatable state
 * @param {Array} props.fields - Available datatable column field definitions
 * @param {Array} props.hiddenFieldNames - Names of columns to hide from view
 * @param {Array} props.tableData - Array of company records to display
 * @param {boolean} props.isLoading - Loading state for skeleton display
 * @returns {JSX.Element} Datatable with company records and interactive controls
 */
const Datatable = ({ props }) => {
  const { fields, hiddenFieldNames, tableData, isLoading } = props;

  useEffect(() => {
    scrollToTop("table-data");
  }, [tableData]);

  return (
    <>
      {isLoading ? (
        <div className="shutdowndatadiv overflow-none">
          <table className="shutdowndatatable" id="table-data" lang="en">
            <thead>
              <tr>
                <th>No.</th>
                <th style={{ color: "transparent" }}>Image</th>

                {fields.map((field, index) => (
                  <th key={index} className={field.cName}>
                    <div className="th-container">
                      {field.displayName}
                      <IoMdArrowDropdown className="sort-navbaricon" />
                    </div>
                  </th>
                ))}

                {/* <th>Data room</th> */}
              </tr>
            </thead>
            <TableRowsLoader rowsNum={18} colsNum={fields.length} />
          </table>
        </div>
      ) : (
        <div className="shutdowndatadiv">
          <table className="shutdowndatatable" id="table-data" lang="en">
            {tableData.results.length === 0 ? (
              <div className="emptytable">
                <img
                  src={NoDataImg}
                  alt="no results found"
                  className="nodataimg"
                />
                <span className="nodatatitle">No results found</span>
                <p className="nodataexp">
                  Try adjusting your search or filter to find what you are
                  looking for.
                </p>
              </div>
            ) : (
              <>
                <thead>
                  <tr>
                    <th>
                      No.<span style={{ float: "right" }}></span>
                    </th>
                    <th style={{ color: "transparent" }}>Image</th>

                    {fields.map((field, index) => (
                      <>
                        {field.displayName === "Founded date" &&
                          !hiddenFieldNames.includes("foundedDate") && (
                            <th key={index} className={field.cName}>
                              <div className="th-container">
                                {field.displayName}
                                {field.displayName === "Founded date" && (
                                  <TimeDropdownComponent
                                    onClick={(value, header) =>
                                      selectedDropdown(
                                        props,
                                        value,
                                        "foundedDate"
                                      )
                                    }
                                  />
                                )}
                              </div>
                            </th>
                          )}
                        {field.displayName === "Shutdown date" &&
                          !hiddenFieldNames.includes("shutdownDate") && (
                            <th key={index} className={field.cName}>
                              <div className="th-container">
                                {field.displayName}
                                {field.displayName === "Shutdown date" && (
                                  <TimeDropdownComponent
                                    onClick={(value, header) =>
                                      selectedDropdown(
                                        props,
                                        value,
                                        "shutdownDate"
                                      )
                                    }
                                  />
                                )}
                              </div>
                            </th>
                          )}

                        {field.displayName === "Operational status" &&
                          !hiddenFieldNames.includes("isActive") && (
                            <th key={index} className={field.cName}>
                              <div className="th-container">
                                {field.displayName}
                                <ActiveDropdownComponent
                                  props={props}
                                  field={field}
                                />
                              </div>
                            </th>
                          )}

                        {!hiddenFieldNames.includes(field.actualName) &&
                          field.displayName !== "Founded date" &&
                          field.displayName !== "Shutdown date" &&
                          field.displayName !== "Operational status" && (
                            <th
                              key={index}
                              className={
                                field.cName === "sticky"
                                  ? "z-1 " + field.cName
                                  : field.cName
                              }
                            >
                              <div className="th-container">
                                {field.displayName}
                                {field.actualName !== "pitchdeck_show" &&
                                  !field.displayName
                                    .toLowerCase()
                                    .includes("date") &&
                                  field.actualName.substr(0, 3) !== "num" &&
                                  field.actualName !== "totalInvestment" &&
                                  field.actualName !== "revenue" &&
                                  field.actualName !== "operationalDuration" && (
                                    <DropdownComponent
                                      props={props}
                                      field={field}
                                      primary={field.actualName === "tradeName"}
                                    />
                                  )}
                                {field.actualName
                                  .toLowerCase()
                                  .includes("date") && (
                                    <>
                                      {field.displayName !== "Founded date" &&
                                        field.displayName !== "Shutdown date" && (
                                          <TimeDropdownComponent
                                            onClick={(value, header) =>
                                              selectedDropdown(
                                                props,
                                                value,
                                                field.actualName
                                              )
                                            }
                                          />
                                        )}
                                      {field.displayName === "Founded date" && (
                                        <TimeDropdownComponent
                                          onClick={(value, header) =>
                                            selectedDropdown(
                                              props,
                                              value,
                                              "foundedDate"
                                            )
                                          }
                                        />
                                      )}
                                      {field.displayName === "Shutdown date" && (
                                        <TimeDropdownComponent
                                          onClick={(value, header) =>
                                            selectedDropdown(
                                              props,
                                              value,
                                              "shutdownDate"
                                            )
                                          }
                                        />
                                      )}
                                    </>
                                  )}
                                {field.actualName.substr(0, 3) === "num" && (
                                  <NumberDropdownComponent
                                    props={props}
                                    field={field}
                                  />
                                )}

                                {field.actualName === "operationalDuration" && (
                                  <NumberDropdownComponent
                                    props={props}
                                    field={field}
                                  />
                                )}

                                {field.actualName === "totalInvestment" && (
                                  <NumberDropdownComponent
                                    props={props}
                                    field={field}
                                  />
                                )}

                                {field.actualName === "revenue" && (
                                  <NumberDropdownComponent
                                    props={props}
                                    field={field}
                                  />
                                )}
                              </div>
                            </th>
                          )}
                      </>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <TableRow props2={props} />
                </tbody>
              </>
            )}
          </table>
        </div>
      )}
    </>
  );
};

export default Datatable;
